/**
 * InFlightCartCard — the polished, user-facing card for an in-flight cart on
 * the RAO landing page.
 *
 * Audience: FSRs + supervisors. They need to identify a cart by **applicant
 * name + stage**, not by GUID or raw code.
 *
 * Design contract (Bill, 2026-04-24):
 *   - NEVER surface cartId, officerCode, branchCode, or any other internal
 *     identifier on the card.
 *   - ALWAYS surface: applicant name, stage, products, branch name, officer
 *     display name, and "X min ago" relative time.
 *   - Whole card is one big tap-target — keyboard- and click-accessible.
 *   - Layout adapts to container width via CSS grid auto-fill (no breakpoint
 *     magic numbers required from callers).
 */

import { useEffect, useRef, useState } from 'react';
import { MoreVertical, FileText } from '@retail-ops/panel-shell/fi';
import { Pill } from '@/components/primitives';
import type { CartDto, Product, Branch } from '@retail-ops/shared-types';
import { TestModeBadge } from '@/modules/_shared/TestModeBadge';
import {
  formatRelativeFriendly,
  humanizeBranch,
  humanizeOfficer,
  summarizePrimaryApplicant,
  summarizeProductChips,
  summarizeStage,
  summarizeStatus,
} from '../utils/cartCardData';
import './InFlightCartCard.css';

export interface InFlightCartCardProps {
  cart: CartDto;
  products: ReadonlyArray<Product>;
  branches: ReadonlyArray<Branch>;
  /** Display name of the currently signed-in user (for officer-name resolution). */
  currentUserDisplayName?: string;
  /** Officer code of the currently signed-in user (drives the "you" highlight). */
  currentUserOfficerCode?: string;
  /** Click handler — receives the cart so the parent can navigate. */
  onOpen: (cart: CartDto) => void;
  /**
   * Optional handler to invoke when the kebab "Close cart…" action is picked.
   * When omitted the kebab menu is hidden — cards stay click-to-open only.
   */
  onCloseCart?: (cart: CartDto) => void;
  /**
   * Optional handler for the kebab "Reassign…" action (Bill 2026-07-27).
   * Only rendered when supplied AND the signed-in user holds a supervisory
   * role — the SERVER is the real gate (Supervisor / Manager / Admin), this
   * just avoids showing an action that would 403.
   */
  onReassign?: (cart: CartDto) => void;
}

const PRODUCT_CHIP_LIMIT = 3;

export function InFlightCartCard({
  cart,
  products,
  branches,
  currentUserDisplayName,
  currentUserOfficerCode,
  onOpen,
  onCloseCart,
  onReassign,
}: InFlightCartCardProps): JSX.Element {
  const applicant = summarizePrimaryApplicant(cart);
  const productChips = summarizeProductChips(cart, products);
  const stage = summarizeStage(cart.pipelineStep);
  const status = summarizeStatus(cart.status);
  const officerName = humanizeOfficer(cart.officerCode, currentUserDisplayName, currentUserOfficerCode);
  const branchName = humanizeBranch(cart.branch, branches);
  const updatedAgo = formatRelativeFriendly(cart.updatedAt);

  const visibleChips = productChips.slice(0, PRODUCT_CHIP_LIMIT);
  const overflowCount = productChips.length - visibleChips.length;
  const hasArtifact = cart.hasRegulatedArtifact === true;

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent | KeyboardEvent): void {
      if (e instanceof KeyboardEvent && e.key === 'Escape') {
        setMenuOpen(false);
        return;
      }
      if (e instanceof MouseEvent) {
        const target = e.target as Node | null;
        if (target && menuRef.current && !menuRef.current.contains(target)) {
          setMenuOpen(false);
        }
      }
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onDoc);
    };
  }, [menuOpen]);

  const subtitle = applicant.isEmpty
    ? 'No applicant yet'
    : applicant.count > 1
      ? `+${applicant.count - 1} co-applicant${applicant.count - 1 === 1 ? '' : 's'}`
      : '';

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      // The kebab uses a real <button> nested inside this clickable card.
      // Switching the card itself back to <button> triggered jsx-a11y's
      // "Interactive controls must not be nested" rule. We instead expose
      // the click target as a generic clickable div with an explicit
      // role+tabIndex+aria-label so screen readers still announce the whole
      // tile as actionable, while the kebab stays a true child <button>.
      role="button"
      tabIndex={0}
      className="iflt-card"
      onClick={(e) => {
        // Don't open the card when clicking inside the kebab menu region.
        const target = e.target as HTMLElement;
        if (target.closest('.iflt-card__kebab')) return;
        onOpen(cart);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const target = e.target as HTMLElement;
          if (target.closest('.iflt-card__kebab')) return;
          e.preventDefault();
          onOpen(cart);
        }
      }}
      aria-label={`Open application for ${applicant.label}, ${stage.label} stage, ${status.label}`}
    >
      <div className="iflt-card__chev" aria-hidden>›</div>

      {(onCloseCart || onReassign) && (
        <div ref={menuRef} className="iflt-card__kebab">
          <button
            type="button"
            className="iflt-card__kebab-trigger"
            aria-haspopup="menu"
            aria-expanded={menuOpen ? 'true' : 'false'}
            aria-label="Cart actions"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
          >
            <MoreVertical size={16} aria-hidden />
          </button>
          {menuOpen && (
            <div role="menu" className="iflt-card__kebab-menu">
              {onReassign && (
                <button
                  type="button"
                  role="menuitem"
                  className="iflt-card__kebab-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onReassign(cart);
                  }}
                >
                  Reassign…
                </button>
              )}
              {onCloseCart && (
                <button
                  type="button"
                  role="menuitem"
                  className="iflt-card__kebab-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onCloseCart(cart);
                  }}
                >
                  Close cart…
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Identity strip */}
      <div className="iflt-card__identity">
        <div
          className={`iflt-card__avatar${applicant.isEmpty ? ' iflt-card__avatar--empty' : ''}`}
          aria-hidden
        >
          {applicant.isEmpty ? '+' : applicant.initials}
        </div>
        <div className="iflt-card__identity-text">
          <div className="iflt-card__name" title={applicant.label}>
            {applicant.label}
          </div>
          {subtitle && <div className="iflt-card__subtitle">{subtitle}</div>}
        </div>
      </div>

      {/* Stage line */}
      <div className="iflt-card__stage">
        <span className="iflt-card__stage-glyph" aria-hidden>{stage.glyph}</span>
        <span className="iflt-card__stage-label">{stage.label}</span>
        <span className="iflt-card__stage-spacer" aria-hidden>·</span>
        <Pill variant={status.variant}>{status.label}</Pill>
        {cart.isTestMode === true && <TestModeBadge active size="sm" />}
        {hasArtifact && (
          <span
            className="iflt-card__artifact"
            title="This cart has regulated artifacts (signed disclosures or posted accounts)"
          >
            <FileText size={12} aria-hidden />
            <span>Has documents</span>
          </span>
        )}
        {/*
          Wave 7 — commit-pipeline timing pills. Each one is humanized via
          formatRelativeFriendly so we never surface a raw ISO timestamp,
          and never surface a cart ID or step key.
        */}
        {cart.status === 'awaiting-signatures' && cart.awaitingSignaturesAt && (
          <Pill variant="warn">
            Signatures pending · {formatRelativeFriendly(cart.awaitingSignaturesAt)}
          </Pill>
        )}
        {cart.status === 'pipeline-error' && cart.lastPipelineErrorAt && (
          <Pill variant="err">
            Last error · {formatRelativeFriendly(cart.lastPipelineErrorAt)}
          </Pill>
        )}
        {cart.status === 'committing' && cart.commitStartedAt && (
          <Pill variant="info">
            Submitted · {formatRelativeFriendly(cart.commitStartedAt)}
          </Pill>
        )}
      </div>

      {/* Products row */}
      <div className="iflt-card__products" aria-label="Products in cart">
        {productChips.length === 0 ? (
          <span className="iflt-card__products-empty">No products selected</span>
        ) : (
          <>
            {visibleChips.map((chip) => (
              <span key={chip.code} className="iflt-card__chip" title={chip.name}>
                {chip.name}
              </span>
            ))}
            {overflowCount > 0 && (
              <span className="iflt-card__chip iflt-card__chip--more">+{overflowCount} more</span>
            )}
          </>
        )}
      </div>

      {/* Footer line */}
      <div className="iflt-card__footer">
        <span className="iflt-card__footer-item" title={`Branch: ${branchName}`}>{branchName}</span>
        <span className="iflt-card__footer-sep" aria-hidden>·</span>
        <span className="iflt-card__footer-item" title={`Officer: ${officerName}`}>{officerName}</span>
        {updatedAgo && (
          <>
            <span className="iflt-card__footer-sep" aria-hidden>·</span>
            <span className="iflt-card__footer-item iflt-card__footer-time">{updatedAgo}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton — used while carts are loading.
// ---------------------------------------------------------------------------

export function InFlightCartCardSkeleton(): JSX.Element {
  return (
    <div className="iflt-card iflt-card--skeleton" aria-hidden>
      <div className="iflt-card__identity">
        <div className="iflt-card__avatar iflt-skel iflt-skel--circle" />
        <div className="iflt-card__identity-text iflt-skel-stack">
          <div className="iflt-skel iflt-skel--line iflt-skel--name" />
          <div className="iflt-skel iflt-skel--line iflt-skel--subtitle" />
        </div>
      </div>
      <div className="iflt-skel iflt-skel--line iflt-skel--stage" />
      <div className="iflt-card__products">
        <div className="iflt-skel iflt-skel--chip" />
        <div className="iflt-skel iflt-skel--chip" />
        <div className="iflt-skel iflt-skel--chip" />
      </div>
      <div className="iflt-skel iflt-skel--line iflt-skel--footer" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grid wrapper — encapsulates the responsive auto-fill grid.
// ---------------------------------------------------------------------------

export interface InFlightCartGridProps {
  children: React.ReactNode;
}

export function InFlightCartGrid({ children }: InFlightCartGridProps): JSX.Element {
  return <div className="iflt-grid">{children}</div>;
}
